import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  actOnPayments,
  daysLeft,
  EMPTY_LEDGER_FILTER,
  formatPaise,
  formatWhen,
  isVoucherCode,
  loadPayments,
  normaliseCode,
  parseRupeesToPaise,
  passStanding,
  rupeesPlain,
  statusLabel,
  voucherBenefit,
  voucherStanding,
  type LedgerFilter,
  type PaymentView,
  type PaymentsPayload,
  type PlanView,
  type WeekBucket,
} from "../../api/payments";
import { download, stampedName, toCsv, type Cell } from "../../api/csv";
import { gatewayModeOf, refundLabel, refundWarning, type GatewayMode } from "../../api/console";
import { Loading } from "../Loading";

/**
 * ===========================================================================
 * PAYMENTS AND SUBSCRIPTIONS. Every row real, and not one rupee of it.
 * ===========================================================================
 *
 * THE WARNING STRIP IS THE FIRST THING BUILT AND THE FIRST THING DRAWN. It is
 * rendered before the fetch resolves, because a refund button that looks real
 * and is not is the most dangerous thing on this screen, and a warning that
 * arrives a beat after the buttons is a warning that can be missed.
 *
 * It reads `movesRealMoney` off the gateway rather than off a constant here,
 * so a live implementation removes the strip by existing, and cannot forget to.
 *
 * NO PRICE IS DRAWN THAT NOBODY TYPED. There is no default in the plan form,
 * no example tier, and no placeholder that looks like a number. `commerce_plan`
 * is empty until the founder decides, and until then this screen says so.
 */

const PANEL = "bg-surface-container-lowest border border-outline-variant rounded-xl";
const LABEL = "font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant";
const FIELD =
  "w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm text-body-sm text-on-surface focus:outline-none focus:ring-1 focus:ring-primary";
const BUTTON =
  "px-3.5 py-2 rounded-lg font-body-sm text-body-sm font-semibold transition-colors disabled:opacity-50";
const PRIMARY = `${BUTTON} bg-primary text-on-primary hover:bg-primary-container`;
const QUIET = `${BUTTON} bg-surface-container border border-outline-variant text-on-surface hover:bg-surface-container-high`;
const DANGER = `${BUTTON} bg-error text-on-error hover:opacity-90`;
const CELL = "py-3 px-3 align-top font-body-sm text-body-sm";
const HEAD = "py-2.5 px-3 text-left font-label-sm text-label-sm uppercase tracking-wider";

export function PaymentsScreen() {
  const [data, setData] = useState<PaymentsPayload | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refunding, setRefunding] = useState<PaymentView | null>(null);
  const [filter, setFilter] = useState<LedgerFilter>(EMPTY_LEDGER_FILTER);

  const reload = useCallback(async () => {
    try {
      setData(await loadPayments(filter));
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "the server did not answer");
    }
  }, [filter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true);
      setNotice(null);
      try {
        const said = await work();
        await reload();
        setNotice(said);
        setFailure(null);
      } catch (error) {
        setFailure(error instanceof Error ? error.message : "the write was refused");
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  // Three states, because Razorpay in test mode is neither a mock nor live.
  const mode = gatewayModeOf(data?.gateway);
  const plans = data?.plans ?? [];
  const sellable = plans.filter((plan) => plan.active);

  const exportLedger = useCallback(() => {
    if (data === null) return;
    download(
      stampedName("prephike-payments", data.now),
      toCsv(
        [
          "payment",
          "created",
          "candidate",
          "plan",
          "amount rupees",
          "refunded rupees",
          "status",
          "gateway",
          "gateway reference",
        ],
        data.payments.map((one): readonly Cell[] => [
          one.paymentId,
          new Date(one.createdAt).toISOString(),
          one.userEmail ?? one.userId,
          one.planName,
          rupeesPlain(one.amountPaise),
          rupeesPlain(one.refundedPaise),
          one.status,
          one.gateway,
          one.gatewayRef,
        ]),
      ),
    );
    setNotice(
      `Exported ${data.payments.length} ledger ${data.payments.length === 1 ? "row" : "rows"}. Every one of them is marked ${data.gateway.name}.`,
    );
  }, [data]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-space-lg">
      <GatewayStrip name={data?.gateway.name ?? null} mode={data === null ? null : mode} />

      <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-space-md border-b border-outline-variant pb-space-md">
        <div className="space-y-1">
          <div className={LABEL}>Financial operations and settlement, INR only</div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold tracking-tight">
            Payments &amp; Subscriptions Control
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant max-w-3xl">
            Plans, the ledger they produce, refunds, vouchers and granted passes. Amounts are stored
            as whole paise and rendered as rupees; nothing on this screen holds a fraction of a
            paisa or a second currency.
          </p>
        </div>
        {/* NOT BEFORE THE READ LANDS. These drew at first paint and acted
            on nothing: an export of an empty ledger, a reload of a screen
            that is already loading. */}
        <div className={data === null ? "hidden" : "flex flex-wrap items-center gap-2"}>
          <button
            type="button"
            className={QUIET}
            onClick={exportLedger}
            disabled={data === null || data.payments.length === 0}
          >
            Export the ledger (CSV)
          </button>
          <button type="button" className={QUIET} onClick={() => void reload()} disabled={busy}>
            Reload
          </button>
        </div>
      </header>

      <Strip tone="error" text={failure} onDismiss={() => setFailure(null)} />
      <Strip tone="ok" text={notice} onDismiss={() => setNotice(null)} />

      {data === null ? (
        <Loading what="Reading the payment ledger." />
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-space-md">
            <Kpi
              label="Captured"
              value={formatPaise(data.totals.grossPaise)}
              note={`${data.totals.payments} payment rows`}
              mode={mode}
            />
            <Kpi
              label="Refunded"
              value={formatPaise(data.totals.refundedPaise)}
              note="Sum of refunded_paise"
              mode={mode}
            />
            <Kpi
              label="Net"
              value={formatPaise(data.totals.netPaise)}
              note="Captured less refunded"
              mode={mode}
            />
            <Kpi
              label="Passes live now"
              value={String(data.passes.liveNow)}
              note={
                data.passes.byStatus.length === 0
                  ? "No subscriptions yet"
                  : data.passes.byStatus.map((one) => `${one.count} ${one.status}`).join(", ")
              }
            />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-lg items-start">
            <div className="lg:col-span-7 min-w-0">
              <WeekChart weeks={data.weeks} mode={mode} />
            </div>
            <div className="lg:col-span-5 min-w-0">
              <Breakdown data={data} mode={mode} />
            </div>
          </div>

          <Plans plans={plans} busy={busy} run={run} />

          <Ledger
            data={data}
            plans={sellable}
            busy={busy}
            mode={mode}
            filter={filter}
            onFilter={setFilter}
            run={run}
            onRefund={setRefunding}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-lg items-start">
            <Vouchers data={data} busy={busy} run={run} />
            <Passes data={data} plans={plans} busy={busy} run={run} />
          </div>

        </>
      )}

      {refunding === null ? null : (
        <RefundDialog
          payment={refunding}
          mode={mode}
          busy={busy}
          onClose={() => setRefunding(null)}
          onConfirm={(amountPaise, reason) => {
            const payment = refunding;
            setRefunding(null);
            void run(async () => {
              await actOnPayments({
                action: "refund",
                paymentId: payment.paymentId,
                amountPaise,
                reason,
              });
              return `${refundLabel(payment.gateway, mode)} of ${formatPaise(amountPaise)} against ${payment.paymentId}. An admin_audit row was written.`;
            });
          }}
        />
      )}
    </div>
  );
}

/**
 * THE THING THAT CANNOT BE MISSED, AND NOT UNTIL IT IS KNOWN.
 *
 * It used to draw before the answer arrived, and `gatewayModeOf(undefined)`
 * returns "mock", so every load of this screen put "MOCK GATEWAY. NO REAL
 * MONEY MOVES." on it for 1.3 seconds and then replaced it with "Razorpay in
 * test mode". That is a withdrawn warning, in the surface where money is
 * inspected, on every single load. A null mode now draws nothing, which is the
 * same rule the candidate Plans screen already follows.
 */
function GatewayStrip({ name, mode }: { readonly name: string | null; readonly mode: GatewayMode | null }) {
  if (mode === null) return null;
  if (mode === "live") {
    return (
      <div className="rounded-xl border-2 border-error bg-error text-on-error px-space-md py-space-md">
        <div className="font-label-md text-label-md uppercase tracking-widest font-semibold">
          Live gateway: {name}
        </div>
        <p className="font-body-md text-body-md mt-1">
          Money moves. A refund issued here reaches a real bank and cannot be taken back from this
          screen.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-error bg-error-container text-on-error-container px-space-md py-space-md">
      <div className="font-label-md text-label-md uppercase tracking-widest font-semibold">
        {mode === "test"
          ? "Razorpay in test mode. No real money moves."
          : `Mock gateway${name === null ? "" : `: ${name}`}. No real money moves.`}
      </div>
      <p className="font-body-md text-body-md mt-1 max-w-4xl">
        {mode === "test" ? (
          <>
            Razorpay is reachable and every call on this screen is a real API call, but the
            credentials are test credentials: only test cards are accepted and no figure here is
            revenue. Rows written now carry{" "}
            <code className="font-label-md text-label-md">gateway = razorpay</code> and are
            refundable through Razorpay. Older rows marked{" "}
            <code className="font-label-md text-label-md">gateway = mock</code> are not, and the
            refund control says which is which.
          </>
        ) : (
          <>
            Every payment, refund, voucher and pass on this screen is written to the live database
            and marked <code className="font-label-md text-label-md">gateway = mock</code>. Nothing
            is sent to Razorpay. No card is charged, no refund reaches anybody&apos;s bank, and no
            figure here is revenue. The rows are real so the screen can be trusted; the money is
            not.
          </>
        )}
      </p>
    </div>
  );
}

function Strip({
  tone,
  text,
  onDismiss,
}: {
  readonly tone: "ok" | "error";
  readonly text: string | null;
  readonly onDismiss: () => void;
}) {
  if (text === null) return null;
  const skin =
    tone === "error"
      ? "bg-error-container text-on-error-container border-error/40"
      : "bg-secondary-container text-on-secondary-container border-secondary/40";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start justify-between gap-space-md rounded-lg border px-space-md py-space-sm font-body-sm text-body-sm ${skin}`}
    >
      <span>{text}</span>
      <button type="button" onClick={onDismiss} className="underline shrink-0">
        Dismiss
      </button>
    </div>
  );
}

function Kpi({
  label,
  value,
  note,
  mode,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string;
  /** Absent on a figure that is a count rather than money. */
  readonly mode?: GatewayMode;
}) {
  return (
    <div className={`${PANEL} p-space-md flex flex-col gap-space-xs`}>
      <div className="flex items-center justify-between gap-space-sm">
        <span className={LABEL}>{label}</span>
        <NotRevenueChip mode={mode} />
      </div>
      <div className="font-headline-md text-headline-md text-on-surface font-bold tracking-tight">
        {value}
      </div>
      <p className="font-body-sm text-body-sm text-on-surface-variant">{note}</p>
    </div>
  );
}

/**
 * MARKS A FIGURE THAT IS NOT REVENUE, and says which kind of not-revenue.
 *
 * This was `MockChip`, drawn whenever `movesRealMoney` was false, so it stamped
 * "mock" over Razorpay test figures that came from real API calls. Two states
 * are not revenue and they are not the same state.
 */
function NotRevenueChip({ mode }: { readonly mode: GatewayMode | null | undefined }) {
  if (mode === null || mode === undefined || mode === "live") return null;
  return (
    <span className="px-1.5 py-0.5 rounded border border-error/50 bg-error-container text-on-error-container font-label-sm text-label-sm uppercase tracking-wider">
      {mode === "test" ? "test" : "mock"}
    </span>
  );
}

function Panel({
  title,
  blurb,
  aside,
  children,
}: {
  readonly title: string;
  readonly blurb: string;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className={`${PANEL} p-space-lg space-y-space-md`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-space-sm">
        <div>
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-semibold">{title}</h2>
          <p className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">{blurb}</p>
        </div>
        {aside === undefined ? null : <div className="shrink-0">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className={`${LABEL} block`}>{label}</span>
      {children}
      {hint === undefined ? null : (
        <span className="block font-body-sm text-body-sm text-on-surface-variant">{hint}</span>
      )}
    </label>
  );
}

type Run = (work: () => Promise<string>) => Promise<void>;

function weekLabel(week: string): string {
  const [year, month, day] = week.split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1)).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * Captured paise, a week at a time. There is no target line and no projection:
 * the bars are the rows, and the tallest one sets the top of the plot.
 */
function WeekChart({ weeks, mode }: { readonly weeks: readonly WeekBucket[]; readonly mode: GatewayMode }) {
  const top = Math.max(...weeks.map((one) => one.capturedPaise), 1);
  const total = weeks.reduce((sum, one) => sum + one.capturedPaise, 0);
  const rows = weeks.reduce((sum, one) => sum + one.payments, 0);

  return (
    <Panel
      title="Captured, a week at a time"
      blurb={`The last ${weeks.length} weeks of the payment ledger, counted from created_at. Weeks with nothing are drawn flat, not skipped.`}
      aside=<NotRevenueChip mode={mode} />
    >
      {rows === 0 ? (
        <Empty
          title="Nothing captured yet"
          body="The chart fills as soon as a capture is recorded below. It draws the rows and nothing else: no target, no projection and no run rate."
        />
      ) : (
        <>
          <div className="flex items-stretch gap-2 h-44">
            {weeks.map((one) => (
              <div key={one.week} className="flex-1 h-full flex flex-col justify-end items-center">
                <span className="font-label-sm text-[11px] text-on-surface-variant tabular-nums pb-1">
                  {one.payments === 0 ? "" : one.payments}
                </span>
                <div
                  className="w-full rounded bg-secondary/80"
                  style={{
                    height: `${Math.max(2, Math.round((one.capturedPaise / top) * 92))}%`,
                  }}
                  title={`${weekLabel(one.week)}: ${formatPaise(one.capturedPaise)} over ${one.payments} rows`}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            {weeks.map((one) => (
              <span
                key={one.week}
                className="flex-1 text-center font-label-sm text-[11px] text-outline"
              >
                {weekLabel(one.week)}
              </span>
            ))}
          </div>
          <p className="font-body-sm text-body-sm text-on-surface-variant border-t border-outline-variant/60 pt-space-sm">
            {formatPaise(total)} captured over {rows} rows in the window. The tallest week is{" "}
            {formatPaise(top)}.
          </p>
        </>
      )}
    </Panel>
  );
}

/** A share of a real total, drawn rather than printed as a percentage. */
function Breakdown({ data, mode }: { readonly data: PaymentsPayload; readonly mode: GatewayMode }) {
  const top = Math.max(...data.breakdown.map((one) => one.capturedPaise), 1);
  return (
    <Panel
      title="By plan"
      blurb="Captured, refunded and net for each plan, and how many passes it is holding up right now."
      aside=<NotRevenueChip mode={mode} />
    >
      {data.breakdown.length === 0 ? (
        <Empty
          title="No plan to break down"
          body="A share of nothing is not a share. This fills the moment a plan exists and a capture lands against it."
        />
      ) : (
        <ul className="space-y-space-sm">
          {data.breakdown.map((one) => (
            <li key={one.planId} className="space-y-1">
              <div className="flex justify-between gap-space-sm font-body-sm text-body-sm">
                <span className="text-on-surface font-semibold truncate">{one.name}</span>
                <span className="text-on-surface tabular-nums whitespace-nowrap">
                  {formatPaise(one.netPaise)}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-surface-container overflow-hidden">
                <div
                  className="h-full rounded-full bg-secondary"
                  style={{ width: `${Math.round((one.capturedPaise / top) * 100)}%` }}
                />
              </div>
              <div className="font-label-sm text-label-sm text-on-surface-variant tabular-nums">
                {formatPaise(one.capturedPaise)} captured, {formatPaise(one.refundedPaise)} refunded, {one.payments}{" "}
                {one.payments === 1 ? "payment" : "payments"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Plans({
  plans,
  busy,
  run,
}: {
  readonly plans: readonly PlanView[];
  readonly busy: boolean;
  readonly run: Run;
}) {
  const [name, setName] = useState("");
  const [blurb, setBlurb] = useState("");
  const [price, setPrice] = useState("");
  const [days, setDays] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const paise = parseRupeesToPaise(price);
  const period = /^\d{1,4}$/.test(days.trim()) ? Number(days.trim()) : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim().length === 0) return setProblem("A plan needs a name.");
    if (paise === null) return setProblem("Price must be rupees, with at most two decimal places.");
    if (period === null || period < 1) return setProblem("A period is a whole number of days.");
    setProblem(null);
    void run(async () => {
      await actOnPayments({
        action: "create_plan",
        name: name.trim(),
        blurb: blurb.trim(),
        amountPaise: paise,
        periodDays: period,
        sortOrder: plans.length,
      });
      setName("");
      setBlurb("");
      setPrice("");
      setDays("");
      return `Created ${name.trim()} at ${formatPaise(paise)} for ${period} days.`;
    });
  }

  return (
    <Panel
      title="Plans"
      blurb="No tier is seeded and none is suggested. A plan exists once somebody here decides what it costs."
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-space-lg">
        <div className="lg:col-span-7 min-w-0">
          {plans.length === 0 ? (
            <Empty
              title="No plans yet"
              body="Nothing can be sold, granted or refunded until a plan exists. Create the first one on the right."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-container text-on-surface-variant">
                  <tr>
                    <th className={HEAD}>Plan</th>
                    <th className={HEAD}>Price</th>
                    <th className={HEAD}>Period</th>
                    <th className={HEAD}>Subscriptions</th>
                    <th className={`${HEAD} text-right`}>State</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/60">
                  {plans.map((plan) => (
                    <tr key={plan.planId}>
                      <td className={CELL}>
                        <div className="font-semibold text-on-surface">{plan.name}</div>
                        <div className="text-on-surface-variant">{plan.blurb}</div>
                      </td>
                      <td className={`${CELL} font-label-md text-label-md`}>
                        {formatPaise(plan.amountPaise)}
                        <div className="text-on-surface-variant">{plan.amountPaise} paise</div>
                      </td>
                      <td className={CELL}>{plan.periodDays} days</td>
                      <td className={CELL}>{plan.subscriptions}</td>
                      <td className={`${CELL} text-right`}>
                        <button
                          type="button"
                          className={QUIET}
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await actOnPayments({
                                action: "set_plan_active",
                                planId: plan.planId,
                                active: !plan.active,
                              });
                              return `${plan.name} is now ${plan.active ? "retired" : "active"}.`;
                            })
                          }
                        >
                          {plan.active ? "Active, retire it" : "Retired, activate it"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <form className="lg:col-span-5 space-y-space-sm" onSubmit={submit}>
          <Field label="Name">
            <input
              className={FIELD}
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={80}
              required
            />
          </Field>
          <Field label="What it includes">
            <input
              className={FIELD}
              value={blurb}
              onChange={(event) => setBlurb(event.target.value)}
              maxLength={200}
            />
          </Field>
          <div className="grid grid-cols-2 gap-space-sm">
            <Field
              label="Price in rupees"
              hint={paise === null ? "Digits, with at most two decimals" : `${paise} paise`}
            >
              <input
                className={FIELD}
                value={price}
                inputMode="decimal"
                onChange={(event) => setPrice(event.target.value)}
                required
              />
            </Field>
            <Field label="Period in days">
              <input
                className={FIELD}
                value={days}
                inputMode="numeric"
                onChange={(event) => setDays(event.target.value)}
                required
              />
            </Field>
          </div>
          {problem === null ? null : (
            <p className="font-body-sm text-body-sm text-error">{problem}</p>
          )}
          <button type="submit" className={PRIMARY} disabled={busy}>
            Create plan
          </button>
        </form>
      </div>
    </Panel>
  );
}

function FilterPill({
  active,
  label,
  onClick,
}: {
  readonly active: boolean;
  readonly label: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        "px-3 py-1.5 rounded-lg font-body-sm text-body-sm border shrink-0 transition-colors",
        active
          ? "bg-primary text-on-primary border-primary font-semibold"
          : "bg-surface-container text-on-surface-variant border-outline-variant hover:text-on-surface",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function Ledger({
  data,
  plans,
  busy,
  mode,
  filter,
  onFilter,
  run,
  onRefund,
}: {
  readonly data: PaymentsPayload;
  readonly plans: readonly PlanView[];
  readonly busy: boolean;
  readonly mode: GatewayMode;
  readonly filter: LedgerFilter;
  readonly onFilter: (next: LedgerFilter) => void;
  readonly run: Run;
  readonly onRefund: (payment: PaymentView) => void;
}) {
  const [email, setEmail] = useState("");
  const [planId, setPlanId] = useState("");
  const payments = data.payments;
  const everything = useMemo(
    () => data.statusMix.reduce((sum, one) => sum + one.count, 0),
    [data.statusMix],
  );

  return (
    <Panel
      title="Ledger"
      blurb="Every payment row, newest first, with the gateway that produced it. The amount always comes from the plan and never from this form."
    >
      <form
        className="flex flex-wrap items-end gap-space-sm bg-surface-container-low border border-outline-variant rounded-lg p-space-md"
        onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            await actOnPayments({ action: "mock_capture", email: email.trim(), planId });
            setEmail("");
            return `Recorded a mock capture for ${email.trim()}. The row is marked gateway = mock.`;
          });
        }}
      >
        <div className="grow min-w-[16rem]">
          <Field label="Candidate email">
            <input
              className={FIELD}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </Field>
        </div>
        <div className="grow min-w-[14rem]">
          <Field label="Plan">
            <select
              className={FIELD}
              value={planId}
              onChange={(event) => setPlanId(event.target.value)}
              required
            >
              <option value="">Choose a plan</option>
              {plans.map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.name}, {formatPaise(plan.amountPaise)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <button type="submit" className={QUIET} disabled={busy || plans.length === 0}>
          Record a mock capture
        </button>
        <p className="w-full font-body-sm text-body-sm text-on-surface-variant">
          This writes a subscription and a captured payment so the ledger and the refund path can be
          exercised. It charges nothing.
        </p>
      </form>

      <div className="flex flex-wrap items-center gap-2">
        <FilterPill
          active={filter.status.length === 0}
          label={`Every status (${everything})`}
          onClick={() => onFilter({ ...filter, status: "", page: 1 })}
        />
        {data.statusMix.map((one) => (
          <FilterPill
            key={one.status}
            active={filter.status === one.status}
            label={`${statusLabel(one.status)} (${one.count})`}
            onClick={() => onFilter({ ...filter, status: one.status, page: 1 })}
          />
        ))}
        {data.gatewayMix.length > 1 && (
          <label className="flex items-center gap-2 font-label-sm text-label-sm text-on-surface-variant">
            <span>Gateway</span>
            <select
              value={filter.gateway}
              onChange={(event) => onFilter({ ...filter, gateway: event.target.value, page: 1 })}
              className="bg-surface-container-low border border-outline-variant rounded px-2 py-1 font-body-sm text-body-sm text-on-surface"
            >
              <option value="">Every gateway</option>
              {data.gatewayMix.map((one) => (
                <option key={one.gateway} value={one.gateway}>
                  {one.gateway} ({one.count})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {payments.length === 0 ? (
        <Empty
          title={filter.status.length === 0 ? "No payments yet" : "Nothing matches that filter"}
          body="The counts on the pills are live, so a zero there is a real zero. The ledger fills as soon as a capture is recorded above."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-container text-on-surface-variant">
              <tr>
                <th className={HEAD}>Payment</th>
                <th className={HEAD}>Candidate</th>
                <th className={HEAD}>Plan</th>
                <th className={HEAD}>Amount</th>
                <th className={HEAD}>Status</th>
                <th className={`${HEAD} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/60">
              {payments.map((payment) => (
                <tr key={payment.paymentId}>
                  <td className={`${CELL} font-label-md text-label-md`}>
                    <div className="text-on-surface">{payment.paymentId.slice(0, 8)}</div>
                    <div className="text-on-surface-variant break-all">
                      {payment.gatewayRef ?? "no reference"}
                    </div>
                    <div className="text-on-surface-variant">{formatWhen(payment.createdAt)}</div>
                  </td>
                  <td className={CELL}>{payment.userEmail ?? payment.userId.slice(0, 8)}</td>
                  <td className={CELL}>{payment.planName ?? "no plan"}</td>
                  <td className={`${CELL} font-label-md text-label-md`}>
                    <div className="text-on-surface">{formatPaise(payment.amountPaise)}</div>
                    {payment.refundedPaise > 0 ? (
                      <div className="text-error">
                        {formatPaise(payment.refundedPaise)} refunded
                      </div>
                    ) : null}
                  </td>
                  <td className={CELL}>
                    <div className="flex flex-wrap items-center gap-1">
                      <StatusChip status={payment.status} />
                      {payment.gateway === "mock" ? <NotRevenueChip mode="mock" /> : null}
                    </div>
                  </td>
                  <td className={`${CELL} text-right`}>
                    {payment.status === "captured" &&
                    payment.refundedPaise < payment.amountPaise ? (
                      <button
                        type="button"
                        className={QUIET}
                        disabled={busy}
                        onClick={() => onRefund(payment)}
                      >
                        {refundLabel(payment.gateway, mode)}
                      </button>
                    ) : (
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        nothing to refund
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-space-sm font-label-md text-label-md text-on-surface-variant border-t border-outline-variant/60 pt-space-sm">
        <span>
          Showing {payments.length} of {data.ledger.total} matching rows, page {data.ledger.page} of{" "}
          {data.ledger.pages}
        </span>
        <span className="inline-flex gap-2">
          <button
            type="button"
            className={QUIET}
            disabled={data.ledger.page <= 1}
            onClick={() => onFilter({ ...filter, page: data.ledger.page - 1 })}
          >
            Previous
          </button>
          <button
            type="button"
            className={QUIET}
            disabled={data.ledger.page >= data.ledger.pages}
            onClick={() => onFilter({ ...filter, page: data.ledger.page + 1 })}
          >
            Next
          </button>
        </span>
      </div>
    </Panel>
  );
}

function StatusChip({ status }: { readonly status: PaymentView["status"] }) {
  const skin =
    status === "captured"
      ? "bg-secondary-container text-on-secondary-container border-secondary/40"
      : status === "refunded"
        ? "bg-error-container text-on-error-container border-error/40"
        : "bg-surface-container-high text-on-surface-variant border-outline-variant";
  return (
    <span
      className={`px-1.5 py-0.5 rounded border font-label-sm text-label-sm uppercase tracking-wider ${skin}`}
    >
      {status}
    </span>
  );
}

function Vouchers({
  data,
  busy,
  run,
}: {
  readonly data: PaymentsPayload;
  readonly busy: boolean;
  readonly run: Run;
}) {
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [percent, setPercent] = useState("");
  const [amount, setAmount] = useState("");
  const [uses, setUses] = useState("");
  const [expiry, setExpiry] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  function expiryAt(): number | null | "bad" {
    if (expiry.trim().length === 0) return null;
    const at = Date.parse(`${expiry}T23:59:59Z`);
    if (Number.isNaN(at) || at <= data.now) return "bad";
    return at;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const clean = normaliseCode(code);
    if (!isVoucherCode(clean)) {
      return setProblem("A code is four to forty characters of A to Z, 0 to 9, dash or underscore.");
    }
    const maxUses = /^\d{1,5}$/.test(uses.trim()) ? Number(uses.trim()) : null;
    if (maxUses === null || maxUses < 1) return setProblem("Max uses is a whole number, one or more.");

    const expiresAt = expiryAt();
    if (expiresAt === "bad") return setProblem("An expiry is a date in the future, or nothing.");

    if (kind === "percent") {
      const off = /^\d{1,3}$/.test(percent.trim()) ? Number(percent.trim()) : null;
      if (off === null || off < 1 || off > 100) return setProblem("A percentage is 1 to 100.");
      setProblem(null);
      return void run(async () => {
        await actOnPayments({
          action: "create_voucher",
          code: clean,
          percentOff: off,
          maxUses,
          expiresAt,
        });
        setCode("");
        setPercent("");
        setUses("");
        setExpiry("");
        return `Created ${clean} at ${off}% off. An admin_audit row was written.`;
      });
    }

    const paise = parseRupeesToPaise(amount);
    if (paise === null || paise === 0) return setProblem("A flat discount is rupees above zero.");
    setProblem(null);
    return void run(async () => {
      await actOnPayments({
        action: "create_voucher",
        code: clean,
        amountOffPaise: paise,
        maxUses,
        expiresAt,
      });
      setCode("");
      setAmount("");
      setUses("");
      setExpiry("");
      return `Created ${clean} at ${formatPaise(paise)} off. An admin_audit row was written.`;
    });
  }

  return (
    <Panel
      title="Vouchers"
      blurb="A percentage off or a flat amount off, never both. The code is stored uppercase and compared uppercase."
    >
      <form className="space-y-space-sm" onSubmit={submit}>
        <Field label="Code">
          <input
            className={`${FIELD} font-label-md text-label-md uppercase`}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            maxLength={40}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label="Benefit">
            <select
              className={FIELD}
              value={kind}
              onChange={(event) => setKind(event.target.value === "amount" ? "amount" : "percent")}
            >
              <option value="percent">Percentage off</option>
              <option value="amount">Flat amount off</option>
            </select>
          </Field>
          {kind === "percent" ? (
            <Field label="Percent off">
              <input
                className={FIELD}
                value={percent}
                inputMode="numeric"
                onChange={(event) => setPercent(event.target.value)}
                required
              />
            </Field>
          ) : (
            <Field label="Rupees off">
              <input
                className={FIELD}
                value={amount}
                inputMode="decimal"
                onChange={(event) => setAmount(event.target.value)}
                required
              />
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label="Max uses">
            <input
              className={FIELD}
              value={uses}
              inputMode="numeric"
              onChange={(event) => setUses(event.target.value)}
              required
            />
          </Field>
          <Field label="Expires" hint="Leave it empty for a code that never lapses">
            <input
              className={FIELD}
              type="date"
              value={expiry}
              onChange={(event) => setExpiry(event.target.value)}
            />
          </Field>
        </div>
        {problem === null ? null : <p className="font-body-sm text-body-sm text-error">{problem}</p>}
        <button type="submit" className={PRIMARY} disabled={busy}>
          Create voucher
        </button>
      </form>

      {data.vouchers.length === 0 ? (
        <Empty title="No vouchers" body="Nothing has been issued." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-container text-on-surface-variant">
              <tr>
                <th className={HEAD}>Code</th>
                <th className={HEAD}>Benefit</th>
                <th className={HEAD}>Redemptions</th>
                <th className={`${HEAD} text-right`}>Standing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/60">
              {data.vouchers.map((voucher) => (
                <tr key={voucher.voucherId}>
                  <td className={`${CELL} font-label-md text-label-md text-on-surface`}>
                    {voucher.code}
                    <div className="text-on-surface-variant">
                      {voucher.createdByEmail === null
                        ? "created by an admin since removed"
                        : `by ${voucher.createdByEmail}`}
                    </div>
                  </td>
                  <td className={CELL}>{voucherBenefit(voucher)}</td>
                  <td className={CELL}>
                    {voucher.usedCount} of {voucher.maxUses}
                    <div className="text-on-surface-variant">
                      {voucher.expiresAt === null
                        ? "no expiry"
                        : `expires ${formatWhen(voucher.expiresAt)}`}
                    </div>
                  </td>
                  <td className={`${CELL} text-right`}>{voucherStanding(voucher, data.now)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="font-label-sm text-label-sm text-on-surface-variant">
        A code is a benefit and a use count. There is no redemption path yet, so nothing increments
        the count and no eligibility rule is enforced.
      </p>
    </Panel>
  );
}

function Passes({
  data,
  plans,
  busy,
  run,
}: {
  readonly data: PaymentsPayload;
  readonly plans: readonly PlanView[];
  readonly busy: boolean;
  readonly run: Run;
}) {
  const [email, setEmail] = useState("");
  const [planId, setPlanId] = useState("");
  const [days, setDays] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <Panel
      title="Granted passes"
      blurb="A pass an admin gave rather than one somebody bought. The subscription records who granted it, and an audit row records it too."
    >
      <form
        className="space-y-space-sm"
        onSubmit={(event) => {
          event.preventDefault();
          const span = /^\d{1,4}$/.test(days.trim()) ? Number(days.trim()) : 0;
          if (span < 1) return setProblem("A pass lasts a whole number of days, one or more.");
          setProblem(null);
          return void run(async () => {
            await actOnPayments({
              action: "grant_pass",
              email: email.trim(),
              planId,
              days: span,
            });
            setEmail("");
            setDays("");
            return `Granted a pass to ${email.trim()} for ${span} days.`;
          });
        }}
      >
        <Field label="Candidate email">
          <input
            className={FIELD}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-space-sm">
          <Field label="Plan">
            <select
              className={FIELD}
              value={planId}
              onChange={(event) => setPlanId(event.target.value)}
              required
            >
              <option value="">Choose a plan</option>
              {plans.map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Days">
            <input
              className={FIELD}
              value={days}
              inputMode="numeric"
              onChange={(event) => setDays(event.target.value)}
              required
            />
          </Field>
        </div>
        {problem === null ? null : <p className="font-body-sm text-body-sm text-error">{problem}</p>}
        <button type="submit" className={PRIMARY} disabled={busy || plans.length === 0}>
          Grant pass
        </button>
      </form>

      {data.subscriptions.length === 0 ? (
        <Empty title="No subscriptions" body="Nobody has a pass, bought or granted." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-container text-on-surface-variant">
              <tr>
                <th className={HEAD}>Candidate</th>
                <th className={HEAD}>Plan</th>
                <th className={HEAD}>Standing</th>
                <th className={HEAD}>Expires</th>
                <th className={`${HEAD} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/60">
              {data.subscriptions.map((one) => (
                <tr key={one.subscriptionId}>
                  <td className={CELL}>{one.userEmail ?? one.userId.slice(0, 8)}</td>
                  <td className={CELL}>{one.planName ?? "no plan"}</td>
                  <td className={CELL}>{passStanding(one, data.now)}</td>
                  <td className={CELL}>
                    {formatWhen(one.expiresAt)}
                    {one.expiresAt > data.now && one.status !== "cancelled" ? (
                      <div className="text-on-surface-variant">
                        {daysLeft(one.expiresAt, data.now)} days left
                      </div>
                    ) : null}
                  </td>
                  <td className={`${CELL} text-right`}>
                    <span className="inline-flex flex-col items-end gap-1.5">
                      <button
                        type="button"
                        className={QUIET}
                        disabled={busy || one.status === "cancelled"}
                        onClick={() =>
                          void run(async () => {
                            await actOnPayments({
                              action: "extend_pass",
                              subscriptionId: one.subscriptionId,
                              days: 30,
                            });
                            return `Extended the pass held by ${one.userEmail ?? one.userId} by 30 days.`;
                          })
                        }
                      >
                        Extend 30 days
                      </button>
                      <button
                        type="button"
                        className={QUIET}
                        disabled={busy || one.status === "cancelled"}
                        onClick={() =>
                          void run(async () => {
                            await actOnPayments({
                              action: "cancel_pass",
                              subscriptionId: one.subscriptionId,
                              reason: "cancelled from the payments deck",
                            });
                            return `Cancelled the pass held by ${one.userEmail ?? one.userId}. No money moved.`;
                          })
                        }
                      >
                        Cancel
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function RefundDialog({
  payment,
  mode,
  busy,
  onClose,
  onConfirm,
}: {
  readonly payment: PaymentView;
  readonly mode: GatewayMode;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (amountPaise: number, reason: string) => void;
}) {
  const remaining = payment.amountPaise - payment.refundedPaise;
  const [amount, setAmount] = useState(
    `${Math.trunc(remaining / 100)}.${String(remaining % 100).padStart(2, "0")}`,
  );
  const [reason, setReason] = useState("");
  const paise = parseRupeesToPaise(amount);
  const valid = paise !== null && paise > 0 && paise <= remaining;

  return (
    <div
      className="fixed inset-0 z-[60] bg-inverse-surface/60 flex items-center justify-center p-space-md"
      role="dialog"
      aria-modal="true"
      aria-label="Refund"
    >
      <div className={`${PANEL} w-full max-w-lg p-space-lg space-y-space-md`}>
        <h3 className="font-headline-sm text-headline-sm text-on-surface font-semibold">
          {refundLabel(payment.gateway, mode)}
        </h3>

        <div className="rounded-lg border-2 border-error bg-error-container text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">
          {refundWarning(payment.gateway, mode)}
        </div>

        <dl className="font-body-sm text-body-sm text-on-surface-variant space-y-1">
          <div className="flex justify-between gap-space-md">
            <dt>Payment</dt>
            <dd className="font-label-md text-label-md text-on-surface">{payment.paymentId}</dd>
          </div>
          <div className="flex justify-between gap-space-md">
            <dt>Candidate</dt>
            <dd className="text-on-surface">{payment.userEmail ?? payment.userId}</dd>
          </div>
          <div className="flex justify-between gap-space-md">
            <dt>Refundable</dt>
            <dd className="font-label-md text-label-md text-on-surface">
              {formatPaise(remaining)}
            </dd>
          </div>
        </dl>

        <Field
          label="Amount in rupees"
          hint={valid ? `${paise} paise` : `Between 0 and ${formatPaise(remaining)}`}
        >
          <input
            className={FIELD}
            value={amount}
            inputMode="decimal"
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label="Reason">
          <input
            className={FIELD}
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
        <p className="font-label-sm text-label-sm text-on-surface-variant">
          A full refund ends the pass it paid for. A partial one does not: the person keeps what
          they still paid for.
        </p>

        <div className="flex items-center justify-end gap-space-sm">
          <button type="button" className={QUIET} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={DANGER}
            disabled={busy || !valid}
            onClick={() => {
              if (paise !== null) onConfirm(paise, reason.trim());
            }}
          >
            {refundLabel(payment.gateway, mode)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Empty({ title, body }: { readonly title: string; readonly body: string }) {
  return (
    <div className="rounded-lg border border-dashed border-outline-variant bg-surface-container-low px-space-md py-space-lg">
      <div className="font-headline-sm text-headline-sm text-on-surface font-semibold">{title}</div>
      <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">{body}</p>
    </div>
  );
}

